import { preloadRouteSkeleton } from '@/components/Router/routeSkeletonRegistry';
import { STOREFRONT_COMPACT_MAX_WIDTH } from '@/constants/layout';

type RouteLoader = () => Promise<unknown>;

const NIGHTWING_ORIGIN = new URL(import.meta.env.VITE_NIGHTWING_URL || 'https://agora.red').origin;
let previewOriginsPreconnected = false;

// El preview de /mi-pagina es un iframe a nightwing (otro origen) que a su vez pide fuentes de
// Google. Abrir esas conexiones apenas hay intención de navegar saca el DNS + TLS del camino
// crítico del primer frame. Se inyecta una sola vez y solo si index.html no lo trae ya.
const preconnectPreviewOrigins = () => {
  if (previewOriginsPreconnected || typeof document === 'undefined') return;
  previewOriginsPreconnected = true;
  const add = (href: string, crossOrigin: boolean) => {
    if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    if (crossOrigin) link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  };
  // La navegación del iframe va con credenciales: un preconnect `crossorigin` abriría un socket
  // del pool anónimo que esa navegación no reutiliza. Las fuentes sí son CORS.
  add(NIGHTWING_ORIGIN, false);
  add('https://fonts.gstatic.com', true);
};

const loadStorefrontRoute = (): Promise<unknown> => {
  preconnectPreviewOrigins();
  // Mismo corte que StoreFrontBuilder: hasta 1279 px monta el editor compacto (el de teléfono).
  const mobile = window.matchMedia(`(max-width: ${STOREFRONT_COMPACT_MAX_WIDTH}px)`).matches;
  // El editor abre en Estilos (DesignPanel) y de ahí va a Personalizar (V3DesignSettings); la
  // galería completa de fondos/tipografías (TemplateGallery) solo la pide "Ver todos…", bajo demanda.
  const deviceEditor = mobile
    ? import('@/containers/StoreFrontBuilder/components/MobileEditor/MobileStoreFrontBuilder')
    : Promise.all([
        import('@/containers/StoreFrontBuilder/components/RailEditor/RailEditor'),
        import('@/containers/StoreFrontBuilder/components/UnifiedEditor/components/ProfileSection/ProfileSection'),
        import('@/containers/StoreFrontBuilder/components/UnifiedEditor/components/TemplateGallery/DesignPanel'),
        import('@/containers/StoreFrontBuilder/components/UnifiedEditor/components/TemplateGallery/V3DesignSettings')
      ]);

  // Además del chunk, el componente lazy del editor queda resuelto (preloadStorefrontEditor):
  // así la ruta no vuelve a suspender al montarlo.
  return Promise.all([
    import('@/containers/StoreFrontBuilder').then((module) => module.preloadStorefrontEditor(mobile)),
    deviceEditor
  ]);
};

// Mismo especificador que el React.lazy de cada ruta en Router.tsx: así el import()
// de acá y el del lazy resuelven al mismo chunk (y el navegador lo baja una sola vez).
const routeLoaders: Array<[RegExp, RouteLoader]> = [
  [/^\/callback\/?$/, () => import('@/containers/Auth0Callback')],
  [/^\/new\/user\/?$/, () => import('@/containers/NewUser')],
  [/^\/(?:login|registro)\/?$/, () => import('@/containers/Login/Login')],
  [/^\/$/, () => import('@/containers/Dashboard')],
  [/^\/agenda(?:\/|$)/, () => import('@/containers/Agenda')],
  [/^\/mi-pagina(?:\/|$)/, loadStorefrontRoute],
  // Destinos del rail y del menú: se precalientan por intención (hover, foco, toque)
  // y, en desktop, de a uno cuando el navegador está ocioso (warmRouteChunksWhenIdle).
  [/^\/finanzas(?:\/|$)/, () => import('@/containers/Finanzas')],
  [/^\/caja\/?$/, () => import('@/containers/CashRegister')],
  [/^\/servicios\/?$/, () => import('@/containers/Services')],
  [/^\/productos\/?$/, () => import('@/containers/Products')],
  [/^\/cursos-online\/?$/, () => import('@/containers/OnlineCourses')],
  [/^\/planesmensuales\/?$/, () => import('@/containers/Subscriptions')],
  [/^\/descargables\/?$/, () => import('@/containers/Downloadables')],
  [/^\/eventos\/?$/, () => import('@/containers/UniqueEvents')],
  [/^\/comunidad\/?$/, () => import('@/containers/Community')],
  [/^\/actividad(?:\/[a-z]+)?\/?$/, () => import('@/containers/Activities')],
  [/^\/clientes\/?$/, () => import('@/containers/Clients')],
  // /analytics: sin loader base acá; el Router registra el de AnalyticsGated (v1 o v2 según el flag).
  [/^\/analytics\/[a-z]+\/?$/, () => import('@/containers/AnalyticsV2')],
  [/^\/promotores\/?$/, () => import('@/containers/Promoters')],
  [/^\/datos-clientes\/?$/, () => import('@/containers/ClientFields/ClientFields')],
  [/^\/fichas\/?$/, () => import('@/containers/ClientForms/ClientForms')],
  [/^\/equipo\/?$/, () => import('@/containers/Team')],
  [/^\/contenido-ig\/?$/, () => import('@/containers/Marketing/ContenidoIG/ContenidoIG')],
  [/^\/descuentos\/?$/, () => import('@/containers/Coupons')],
  [/^\/precios-dinamicos\/?$/, () => import('@/containers/Marketing/DynamicPricing')],
  [/^\/ofertas\/?$/, () => import('@/containers/Marketing/Offers')],
  [/^\/fidelizacion\/?$/, () => import('@/containers/Marketing/Loyalty')],
  [/^\/premios(\/.*)?$/, () => import('@/containers/Marketing/Premios')],
  [/^\/resenas\/?$/, () => import('@/containers/Reviews')],
  [/^\/tarjetas-de-regalo\/?$/, () => import('@/containers/GiftCards')],
  [/^\/seguimiento\/?$/, () => import('@/containers/Marketing/Tracking')],
  [/^\/referidos\/?$/, () => import('@/containers/Invitations')],
  [/^\/ideas\/?$/, () => import('@/containers/Ideas')],
  [/^\/mis-cursos\/?$/, () => import('@/containers/MyCourses')],
  [/^\/mis-comunidades\/?$/, () => import('@/containers/MyCommunities')],
  [/^\/facturacion\/?$/, () => import('@/containers/Billing')],
  [/^\/planes\/?$/, () => import('@/containers/Plans')],
  [/^\/ajustes(?:\/info)?\/?$/, () => import('@/containers/Settings/PersonalInfo')],
  [/^\/ajustes\/medios-de-cobro\/?$/, () => import('@/containers/PaymentGateways')],
  [/^\/ajustes\/mensajes-automaticos\/?$/, () => import('@/containers/Settings/MessagesAutomaticos')]
];

// El Router registra acá el `preload` de sus componentes de ruta (lazyWithPreload). Precalentar
// por ese camino deja el componente ya resuelto: al montar la ruta se renderiza sin suspender
// y React no le suma su espera de 300 ms tras el fallback. Si la ruta también tiene un loader
// propio arriba (Mi página baja además el editor del dispositivo), corren los dos.
const registeredLoaders: Array<[RegExp, RouteLoader]> = [];

/** `entries`: path de react-router (con `:param`) y el `preload` del componente de esa ruta. */
export const registerRoutePreloads = (entries: Array<[string, RouteLoader]>): void => {
  for (const [path, preload] of entries) {
    const pattern = new RegExp(`^${path.replace(/:[^/]+/g, '[^/]+')}/?$`);
    const sample = path.replace(/:[^/]+/g, 'x');
    const base = routeLoaders.find(([candidate]) => candidate.test(sample))?.[1];
    registeredLoaders.push([pattern, base ? () => Promise.all([preload(), base()]) : preload]);
  }
};

const findLoader = (target: string): RouteLoader | undefined => {
  const pathname = new URL(target || '/', location.origin).pathname;
  return (
    registeredLoaders.find(([pattern]) => pattern.test(pathname))?.[1] ??
    routeLoaders.find(([pattern]) => pattern.test(pathname))?.[1]
  );
};

/**
 * Starts downloading the code for a route the user is about to open. Hover, focus
 * and touch are cheap signals of intent, so every rail/menu destination is covered.
 */
const preloadRouteChunk = (target: string): Promise<unknown> | undefined =>
  // Native import() already deduplicates loading/evaluation for the same module.
  findLoader(target)?.();

/** Fire-and-forget event handler helper; chunk failures are retried by React.lazy. */
export const warmRouteChunk = (target: string): void => {
  void preloadRouteSkeleton(target.split(/[?#]/)[0]);
  preloadRouteChunk(target)?.catch(() => {});
};

/**
 * Props de intención para un link a `target`: el puntero que entra, el que aprieta (el clic
 * llega ~100 ms después, al soltar), el foco de teclado y el toque arrancan la descarga de
 * la ruta. Mismo set en el rail, el menú mobile, el menú de perfil y el logo.
 */
export const routeWarmProps = (target: string) => {
  const warm = () => warmRouteChunk(target);
  return { onPointerEnter: warm, onPointerDown: warm, onFocus: warm, onTouchStart: warm };
};

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

type NetworkInformationLike = { saveData?: boolean; effectiveType?: string };

const warmedOnIdle = new Set<RouteLoader>();

// Solo los destinos diarios se precalientan en ocioso. Con todo el rail (unas 40
// rutas) el arranque en desktop bajaba ~260 archivos / 6 MB sin descomprimir; el
// resto se sigue precalentando al pasar el mouse, enfocar o tocar el ítem.
const IDLE_WARM_PREFIXES = ['/agenda', '/clientes', '/finanzas', '/servicios', '/actividad', '/mi-pagina'];
const IDLE_WARM_LIMIT = 6;

// La precarga ociosa arranca recién con la página asentada: después del `load`, con
// QUIET_MS sin requests nuevos ni candidatos de LCP (los datos de la pantalla también son
// requests), o con la primera interacción. `requestIdleCallback` solo no alcanzaba: el
// navegador queda ocioso mientras espera la API, y los ~100 módulos de las rutas diarias
// se bajaban y evaluaban justo cuando llegaban los datos (Inicio en frío: 199 JS contra 90
// y el LCP ~0,4–1 s más tarde). SETTLE_CAP_MS es el tope si algo sondea sin parar.
const QUIET_MS = 2500;
const SETTLE_CAP_MS = 15000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown'] as const;

let pageSettledOnce = false;

const whenPageSettled = (onSettled: () => void): (() => void) => {
  let done = false;
  let quietTimer = 0;
  let capTimer = 0;
  let observer: PerformanceObserver | undefined;

  const cleanup = () => {
    window.clearTimeout(quietTimer);
    window.clearTimeout(capTimer);
    observer?.disconnect();
    window.removeEventListener('load', start);
    INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, finish, true));
  };
  function finish() {
    if (done) return;
    done = true;
    pageSettledOnce = true;
    cleanup();
    onSettled();
  }
  const arm = () => {
    window.clearTimeout(quietTimer);
    quietTimer = window.setTimeout(finish, QUIET_MS);
  };
  function start() {
    arm();
    capTimer = window.setTimeout(finish, SETTLE_CAP_MS);
    try {
      observer = new PerformanceObserver(arm);
      observer.observe({ type: 'resource' });
      observer.observe({ type: 'largest-contentful-paint' });
    } catch {
      // Sin PerformanceObserver: queda el silencio contado desde el load y el tope.
    }
  }

  INTERACTION_EVENTS.forEach((type) =>
    window.addEventListener(type, finish, { capture: true, passive: true })
  );
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });

  return () => {
    done = true;
    cleanup();
  };
};

// Destinos probables desde cada pantalla (los recorridos diarios del panel). Se precalientan
// apenas la pantalla actual pintó su contenido, sin esperar a que se asiente: con la cola
// asentada sola (load + QUIET_MS), Agenda → Clientes en desktop pedía el chunk en el clic
// y tardaba 200–300 ms más que antes. El resto de los destinos diarios sigue esperando.
const LIKELY_NEXT: Array<[RegExp, string[]]> = [
  [/^\/$/, ['/agenda', '/clientes']],
  [/^\/agenda(?:\/|$)/, ['/clientes', '/']],
  [/^\/clientes\/?$/, ['/agenda']]
];
const DEFAULT_LIKELY_NEXT = ['/agenda'];
const LIKELY_NEXT_LIMIT = 2;

const SKELETON_SELECTOR = '.frost-shimmer, .frost-skeleton';
const CONTENT_POLL_MS = 250;
const CONTENT_WAIT_CAP_MS = 10000;
let mainContentLcpSeen = false;

const supportsLcp = (): boolean => {
  try {
    return PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint') ?? false;
  } catch {
    return false;
  }
};

const skeletonInView = (main: HTMLElement): boolean =>
  Array.from(main.querySelectorAll(SKELETON_SELECTOR)).some((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
  });

/**
 * Llama a `onPainted` con el navegador ocioso cuando la pantalla ya pintó su contenido:
 * en la carga inicial, después de un candidato de LCP dentro de `#main` (nunca antes del
 * LCP) y sin esqueletos a la vista en `#main` (los datos ya llegaron). En una navegación
 * interna no hay LCP: alcanza con que el contenido esté. Si nunca se da (error, pantalla
 * vacía), no llama: queda la cola asentada.
 */
const whenMainContentPainted = (onPainted: () => void): (() => void) => {
  const w = window as IdleWindow;
  let done = false;
  let timer = 0;
  let idle = 0;
  let waited = 0;
  let observer: PerformanceObserver | undefined;
  const lcpGate = () => mainContentLcpSeen || pageSettledOnce || !supportsLcp();

  const check = () => {
    if (done) return;
    const main = document.getElementById('main');
    if (lcpGate() && main && !skeletonInView(main)) {
      done = true;
      observer?.disconnect();
      idle = w.requestIdleCallback
        ? w.requestIdleCallback(onPainted, { timeout: 1000 })
        : window.setTimeout(onPainted, 200);
      return;
    }
    waited += CONTENT_POLL_MS;
    if (waited < CONTENT_WAIT_CAP_MS) timer = window.setTimeout(check, CONTENT_POLL_MS);
  };

  if (!lcpGate()) {
    try {
      observer = new PerformanceObserver((list) => {
        const inMain = list
          .getEntries()
          .some(
            (entry) => !!(entry as PerformanceEntry & { element?: Element | null }).element?.closest('#main')
          );
        if (inMain) {
          mainContentLcpSeen = true;
          observer?.disconnect();
        }
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch {
      // Sin LCP observable no hay contra qué esperar: queda la cola asentada.
      done = true;
    }
  }
  check();

  return () => {
    done = true;
    observer?.disconnect();
    window.clearTimeout(timer);
    if (w.cancelIdleCallback) w.cancelIdleCallback(idle);
    else window.clearTimeout(idle);
  };
};

/**
 * Precalienta, de a un chunk por vez y solo con el navegador ocioso, las rutas de
 * `targets` (los destinos del rail) en dos tandas:
 *  1. los destinos probables desde `pathname` (LIKELY_NEXT), apenas la pantalla pintó
 *     su contenido (whenMainContentPainted);
 *  2. el resto de los destinos diarios, recién cuando la página se asentó (whenPageSettled).
 * Solo en desktop con una conexión que no sea lenta ni en ahorro de datos: en un celular
 * en 4G bajar el menú cuesta más que lo que ahorra, ahí alcanza con el precalentado por
 * toque. Devuelve la cancelación (para el cleanup del efecto que lo llama, que vuelve a
 * correr en cada navegación).
 */
export const warmRouteChunksWhenIdle = (targets: string[], pathname = location.pathname): (() => void) => {
  const w = window as IdleWindow;
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  const slow = !!connection?.saveData || /(^|-)2g$|^3g$/.test(connection?.effectiveType ?? '');
  if (slow || !window.matchMedia('(min-width: 1024px)').matches) return () => {};

  const toLoaders = (links: string[]) =>
    [...new Set(links.map(findLoader).filter((l): l is RouteLoader => !!l))].filter(
      (loader) => !warmedOnIdle.has(loader)
    );
  const current = findLoader(pathname);
  const likelyLinks = (
    LIKELY_NEXT.find(([pattern]) => pattern.test(pathname))?.[1] ?? DEFAULT_LIKELY_NEXT
  ).filter((link) => targets.includes(link));
  const likely = toLoaders(likelyLinks)
    .filter((loader) => loader !== current)
    .slice(0, LIKELY_NEXT_LIMIT);
  const daily = targets.filter((target) => IDLE_WARM_PREFIXES.some((prefix) => target.startsWith(prefix)));
  const rest = toLoaders(daily).slice(0, IDLE_WARM_LIMIT);

  let cancelled = false;
  let running = false;
  let handle = 0;
  const pending: RouteLoader[] = [];
  const schedule = (cb: () => void) =>
    w.requestIdleCallback ? w.requestIdleCallback(cb, { timeout: 4000 }) : window.setTimeout(cb, 1200);
  const pump = () => {
    const loader = pending.shift();
    if (cancelled || !loader) {
      running = false;
      return;
    }
    running = true;
    handle = schedule(() => {
      if (cancelled) return;
      if (warmedOnIdle.has(loader)) return pump();
      warmedOnIdle.add(loader);
      // El siguiente espera a que termine este: nunca más de una descarga en paralelo.
      loader()
        .catch(() => {})
        .finally(pump);
    });
  };
  const enqueue = (loaders: RouteLoader[]) => {
    pending.push(...loaders.filter((loader) => !warmedOnIdle.has(loader) && !pending.includes(loader)));
    if (!running) pump();
  };
  const cancelPainted = likely.length ? whenMainContentPainted(() => enqueue(likely)) : () => {};
  const cancelSettled = whenPageSettled(() => enqueue(rest));

  return () => {
    cancelled = true;
    cancelPainted();
    cancelSettled();
    if (w.cancelIdleCallback) w.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
};
